<template>
  <div id="release-note">
    <v-data-table
      :headers="header"
      :items="items"
      :options.sync="options"
      :items-per-page="options.itemsPerPage"
      :loading="loading"
      height="80vh"
      class="elevation-1 mb-5 draggable-table"
      :server-items-length="total"
      :footer-props="{'items-per-page-options': [15, 25, 50, 100]}"
      @update:options="getReleaseNote()">
      <template #[`item.type`]="{ item }">
        <v-chip
          class="mb-1"
          :color="getReleaseNoteTypeColor(item.type)"
          text-color="white"
          small>
          {{ item.type }}
        </v-chip>
      </template>
      <template #[`item.roles`]="{ item }">
        <div class="d-flex flex-wrap">
          <v-chip
            v-for="(role, index) in item.roles"
            :key="`tag-selected-chip-${index}`"
            class="ma-1"
            small>
            {{ role }}
          </v-chip>
        </div>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-icon
          class="mr-2"
          small
          @click="editItem(item)">
          mdi-pencil
        </v-icon>
        <gw-icon-hover
          class="sku-table-bin"
          icon-name="mdi-delete"
          icon-hover="mdi-delete-empty"
          small
          @click="removeReleaseNote(item)" />
      </template>
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>
            <span class="capitalize">
              Release Note
            </span>
          </v-toolbar-title>
          <v-divider
            class="mx-4"
            inset
            vertical />
          <v-spacer />
          <v-btn
            color="primary"
            dark
            class="mb-2"
            width="110px"
            @click="dialogToggle()">
            New Item
          </v-btn>
          <v-dialog
            v-model="dialog"
            max-width="600px">
            <v-card>
              <validation-observer
                ref="attributeForm"
                tag="form"
                @submit.prevent="save()">
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>
                <v-card-text>
                  <v-container>
                    <v-row v-if="dialog">
                      <v-col :cols="12">
                        <validation-provider
                          v-slot="{ errors }"
                          :name="'Date'"
                          :rules="{ required: true }">
                          <DatePicker
                            v-model="formData.dateString"
                            label="Date"
                            :readonly="modalLoading"
                            error-messages="errors" />
                          <span class="red--text">{{ errors[0] }}</span>
                        </validation-provider>
                      </v-col>
                      <v-col :cols="12">
                        <gw-text-field
                          v-model="formData.title"
                          :rules="textFieldRules"
                          :readonly="modalLoading"
                          label="Title" />
                      </v-col>
                      <v-col :cols="12">
                        <gw-textarea
                          v-model="formData.detail"
                          :readonly="modalLoading"
                          :rules="textFieldRules"
                          label="Detail" />
                      </v-col>
                      <v-col
                        cols="12"
                        class="card-title">
                        Images
                      </v-col>

                      <div class="image-upload-container">
                        <!-- Preview Images -->
                        <v-row dense>
                          <v-col
                            v-for="(image, index) in formData.images"
                            :key="index"
                            cols="4"
                            class="mb-3">
                            <v-card
                              class="image-card"
                              outlined>
                              <v-img
                                :src="image"
                                class="image-preview"
                                height="170px"
                              ></v-img>
                              <v-btn
                                icon
                                color="red"
                                class="delete-btn"
                                @click="removeImage(index)">
                                <v-icon>mdi-delete</v-icon>
                              </v-btn>
                            </v-card>
                          </v-col>
                          <!-- Upload New File -->
                          <v-col
                            cols="4"
                            class="upload-card">
                            <v-card
                              outlined
                              class="d-flex flex-column justify-center align-center upload-area">
                              <label
                                for="image-upload"
                                style="height: 100%; width: 180px; cursor: pointer; align-content: center;">
                                <v-icon
                                  large
                                  color="grey">mdi-image-plus</v-icon>
                                <p>Click file for upload</p>
                              </label>
                              <input
                                id="image-upload"
                                type="file"
                                multiple
                                accept="image/*"
                                style="display: none;"
                                @change="handleFileChange" />
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>
                      <v-col :cols="12">
                        <gw-select
                          v-model="formData.type"
                          :rules="textFieldRules"
                          :items="types"
                          :readonly="modalLoading"
                          :menu-props="{ offsetY: true }"
                          label="Type"
                          item-text="name" />
                      </v-col>
                      <v-col :cols="12">
                        <gw-autocomplete
                          v-model="formData.roles"
                          :items="roles"
                          :readonly="modalLoading"
                          label="Roles"
                          multiple
                          small-chips
                          clearable
                          deletable-chips />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="closeFormModal()">
                    Cancel
                  </v-btn>
                  <v-btn
                    :loading="modalLoading"
                    color="blue darken-1"
                    type="submit"
                    text>
                    Save
                  </v-btn>
                </v-card-actions>
              </validation-observer>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ReleaseNoteProvider from '@/resources/ReleaseNoteProvider'
import DatePicker from '@/components/DatePicker.vue'

const ReleaseNoteService = new ReleaseNoteProvider()

export default {
  components: {
    DatePicker
  },
  data () {
    return {
      dialog: false,
      loading: false,
      itemsLength: 0,
      pageCount: 0,
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['dateString'],
        sortDesc: [true]
      },
      header: [
        {
          text: 'Type',
          value: 'type',
          width: 120,
          sortable: false
        },
        {
          text: 'Date',
          value: 'dateString',
          width: 120,
          sortable: false
        },
        {
          text: 'Title',
          value: 'title',
          width: 200,
          sortable: false
        },
        {
          text: 'Acknowledge',
          value: 'acknowledge',
          width: 120,
          sortable: false
        },
        {
          text: 'Roles',
          value: 'roles',
          sortable: false
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          width: 100,
          align: 'center'
        }
      ],
      items: [],
      isEdited: false,
      formData: {
        type: 'store',
        priority: 99999,
        images: []
      },
      textFieldRules: {
        required: true
      },
      numberFieldRules: {
        required: true,
        regex: /^[0-9]+$/
      },
      modalLoading: false,
      roles: [
        'non-specify',
        'management',
        'area_manager',
        'marketing',
        'developer',
        'designer',
        'inventory_and_costing',
        'merchandising_planner',
        'merchandiser',
        'creative',
        'graphic',
        'online_admin',
        'office_admin',
        'accounting_manager',
        'accounting',
        'support',
        'warehouse_manager',
        'warehouse',
        'store_manager',
        'acting_assist_store_manager',
        'assist_store_manager',
        'vm',
        'sales_staff'
      ],
      types: [
        {
          name: 'FEATURE',
          value: 'FEATURE',
          color: '#4CAF50'
        },
        {
          name: 'CHANGED',
          value: 'CHANGED',
          color: '#9C27B0'
        },
        {
          name: 'FIXED',
          value: 'FIXED',
          color: '#E91E63'
        },
        {
          name: 'ISSUE',
          value: 'ISSUE',
          color: '#F44336'
        },
        {
          name: 'ANNOUNCEMENT',
          value: 'ANNOUNCEMENT',
          color: '#03A9F4'
        },
        {
          name: 'NOTE',
          value: 'NOTE',
          color: '#607D8B'
        }
      ]
    }
  },
  computed: {
    formTitle () {
      return this.isEdited ? 'Edit ReleaseNote' : 'New ReleaseNote'
    },
    search () {
      return this.$route.query.search ? this.$route.query.search : ''
    }
  },
  created () {
    this.getReleaseNote()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage',
      setModal: 'Components/setModal',
      createReleaseNote: 'Notification/createReleaseNote'
    }),
    getReleaseNoteTypeColor (type) {
      const colors = {}
      this.types.forEach((t) => { colors[t.name] = t.color })

      return colors[type] || '#607D8B'
    },
    setPageCount (event) {
      this.pageCount = event
    },
    async getReleaseNote () {
      try {
        this.loading = true
        const { data: { results, total } } = await ReleaseNoteService.getReleaseNote({
          role: 'all', sortBy: 'dateString', sortOrder: 'desc', page: this.options.page, limit: this.options.itemsPerPage
        })

        for (let i = 0; i < results.length; i++) {
          results[i].acknowledge = `${results[i].releaseNotesAccepted.acknowledgedCount}/${results[i].releaseNotesAccepted.userCount}`
        }

        this.total = total
        this.items = results
      } catch (error) {
        console.error('getReleaseNote: ', error)
        this.setErrorPage(error.code)
      } finally {
        this.loading = false
      }
    },
    dialogToggle () {
      this.isEdited = false
      this.formData = {
        type: 'store',
        priority: 99999,
        images: []
      }
      this.dialog = true
    },
    closeFormModal () {
      this.dialog = false
      this.isEdited = false
      this.formData = {
        priority: 99999
      }
    },
    async editItem (item) {
      try {
        this.loading = true
        const { data } = await ReleaseNoteService.getItemById(item.id)

        this.isEdited = true
        this.formData = {
          id: data.id,
          type: data.type,
          title: data.title,
          detail: data.detail,
          dateString: data.dateString,
          roles: data.roles,
          images: data.images || []
        }
        this.dialog = true
      } catch (error) {
        console.error('Error fetching item:', error)
        this.setSnackbar({
          value: true,
          message: `Error fetching item: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    async handleFileChange (event) {
      const { files } = event.target
      if (!files || files.length === 0) return

      const base64Array = await Promise.all(
        Array.from(files).map((file) => this.convertToBase64(file))
      )

      this.formData.images.push(...base64Array)
    },
    convertToBase64 (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (event) => resolve(event.target.result)
        reader.onerror = (error) => reject(error)
        reader.readAsDataURL(file)
      })
    },
    removeImage (index) {
      this.formData.images.splice(index, 1)
    },
    async save () {
      this.modalLoading = true
      const valid = await this.$refs.attributeForm.validate()
      if (valid) {
        let isSuccess = false
        try {
          const form = this.formData

          if (!form.images || form.images.length === 0) {
            form.images = []
          }

          if (this.isEdited) {
            isSuccess = await this.updateReleaseNote(form)
          } else {
            isSuccess = await this.createReleaseNote(form)
          }
          this.closeFormModal()
        } catch (error) {
          this.setSnackbar({
            value: true,
            message: `Error code ${error.code}: ${error.message}`,
            type: 'error'
          })
          console.error('saveAttribute', error)
        } finally {
          if (isSuccess) {
            this.setSnackbar({
              value: true,
              message: 'Success',
              type: 'success'
            })
            this.getReleaseNote()
          }
        }
      }
      this.modalLoading = false
    },
    async createReleaseNote (item) {
      const { data } = await ReleaseNoteService.createReleaseNote(item)
      this.$store.dispatch('Notification/fetchUnacceptedCount')
      return !!data.id
    },
    async updateReleaseNote (item) {
      const { data } = await ReleaseNoteService.updateReleaseNote(item.id, item)
      this.$store.dispatch('Notification/fetchUnacceptedCount')
      return !!data.id
    },
    async removeReleaseNote (item) {
      this.setModal({
        value: true,
        title: 'Remove Release Note',
        message: 'Do you want to remove the Release Note',
        confirmText: 'Remove',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.confirmRemoveReleaseNote(item.id)
      })
    },
    async confirmRemoveReleaseNote (id) {
      try {
        const { data } = await ReleaseNoteService.deleteReleaseNote(id)
        if (data.id && data.status === 'deleted') {
          this.setSnackbar({
            value: true,
            message: 'Success',
            type: 'success'
          })
          if (this.options.page > 1 && this.items.length < 2) {
            this.options.page--
          } else {
            this.getReleaseNote()
          }
        } else {
          const error = {
            code: 400,
            message: 'Release Note ID not found or status not change'
          }
          throw error
        }
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: `Error code ${error.code}`,
          type: 'error'
        })
        console.error('confirmRemoveReleaseNote', error)
      }
    }
  }
}
</script>

<style scoped>
.vertical-top {
  vertical-align: top;
}

.theme--light.v-data-table.draggable-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > td:not(.v-data-table__mobile-row),
.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > th:not(.v-data-table__mobile-row) {
  border-bottom: 0px solid rgba(0, 0, 0, 0.12);
}
.image-upload-container {
  border: 1px dashed #ccc;
  padding: 10px;
  border-radius: 8px;
  overflow-y: auto;
  width: 100%;
}
.image-card {
  position: relative;
  text-align: center;
}
.image-preview {
  border-radius: 8px;
  object-fit: cover;
}
.upload-card {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 180px
}
.upload-area {
  cursor: pointer;
  text-align: center;
  height: 100%;
  border: 1px dashed #ccc;
  background-color: #f9f9f9;
}
.upload-area p {
  margin: 0;
  font-size: 14px;
  color: grey;
}
.image-upload {
  background: red;
}
.delete-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  z-index: 10;
}
.delete-btn:hover {
  transform: scale(1.1);
}
</style>
